import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { paymentService } from '../../services/paymentService';
import PaymentStatusTimeline from './components/PaymentStatusTimeline';
import TransactionDetails from './components/TransactionDetails';
import PaymentActions from './components/PaymentActions';
import { 
  CheckCircle, 
  XCircle, 
  Clock, 
  AlertTriangle, 
  RefreshCw,
  Download,
  MessageCircle 
} from 'lucide-react';

const PaymentStatusTracking = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [paymentData, setPaymentData] = useState(null);
  const [orderData, setOrderData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [statusSubscription, setStatusSubscription] = useState(null);

  useEffect(() => {
    const paymentId = searchParams?.get('payment');
    const status = searchParams?.get('status');
    
    if (paymentId) {
      loadPaymentData(paymentId);
      setupRealtimeUpdates(paymentId);
    } else {
      navigate('/customer-portal');
    }

    return () => {
      if (statusSubscription) {
        statusSubscription?.unsubscribe();
      }
    };
  }, [searchParams]);

  const loadPaymentData = async (paymentId) => {
    setLoading(true);
    setError('');
    
    try {
      const { data, error } = await paymentService?.getPaymentById(paymentId);
      if (error) {
        throw new Error(error);
      }
      
      setPaymentData(data);
      setOrderData(data?.orders);
    } catch (err) {
      setError('Failed to load payment details: ' + err?.message);
    }
    
    setLoading(false);
  };

  const setupRealtimeUpdates = (paymentId) => {
    const subscription = paymentService?.subscribeToPaymentUpdates(paymentId, (payload) => {
      if (payload?.new) {
        setPaymentData(prevData => ({
          ...prevData,
          ...payload?.new
        }));
      }
    });
    
    setStatusSubscription(subscription);
  };

  const handleRefresh = async () => {
    if (!paymentData?.id) return;
    
    setRefreshing(true);
    await loadPaymentData(paymentData?.id);
    setRefreshing(false);
  };

  const handleRetryPayment = async () => {
    if (!orderData?.id) return;
    
    navigate(`/payment-checkout?order=${orderData?.id}&retry=true`);
  };

  const handleDownloadReceipt = () => {
    // Mock receipt download - implement actual receipt generation
    const receiptData = {
      payment: paymentData,
      order: orderData,
      timestamp: new Date()?.toISOString()
    };
    
    const blob = new Blob([JSON.stringify(receiptData, null, 2)], {
      type: 'application/json'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `receipt-${paymentData?.gateway_reference || paymentData?.id}.json`;
    document.body?.appendChild(a);
    a?.click();
    document.body?.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-16 w-16 text-green-500" />;
      case 'failed': case'cancelled':
        return <XCircle className="h-16 w-16 text-red-500" />;
      case 'processing':
        return <RefreshCw className="h-16 w-16 text-blue-500 animate-spin" />;
      case 'refunded':
        return <RefreshCw className="h-16 w-16 text-purple-500" />;
      default:
        return <Clock className="h-16 w-16 text-yellow-500" />;
    }
  };

  const getStatusMessage = (status) => {
    const messages = {
      pending: {
        title: 'Payment Pending',
        description: 'Your payment is being processed. Please wait while we confirm the transaction.',
        color: 'text-yellow-700'
      },
      processing: {
        title: 'Processing Payment',
        description: 'Your payment is currently being processed by our secure payment system.',
        color: 'text-blue-700'
      },
      completed: {
        title: 'Payment Successful!',
        description: 'Your payment has been successfully processed. Thank you for your business!',
        color: 'text-green-700'
      },
      failed: {
        title: 'Payment Failed',
        description: 'Unfortunately, your payment could not be processed. Please try again or contact support.',
        color: 'text-red-700'
      },
      cancelled: {
        title: 'Payment Cancelled',
        description: 'The payment was cancelled. You can try again or choose a different payment method.',
        color: 'text-gray-700'
      },
      refunded: {
        title: 'Payment Refunded',
        description: 'Your payment has been refunded. Please allow 3-5 business days for the refund to appear.',
        color: 'text-purple-700'
      }
    };
    
    return messages?.[status] || messages?.pending;
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-16 w-16 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading payment details...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6 text-center">
          <AlertTriangle className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Restricted</h2>
          <p className="text-gray-600 mb-6">Please sign in to view payment details.</p>
          <button
            onClick={() => navigate('/login?redirect=/payment-status-tracking')}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700"
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6 text-center">
          <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Payment</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="space-x-4">
            <button
              onClick={handleRefresh}
              className="bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700"
            >
              Try Again
            </button>
            <button
              onClick={() => navigate('/customer-portal')}
              className="bg-gray-600 text-white py-2 px-4 rounded-md hover:bg-gray-700"
            >
              Go to Portal
            </button>
          </div>
        </div>
      </div>
    );
  }

  const statusInfo = getStatusMessage(paymentData?.status);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            {getStatusIcon(paymentData?.status)}
          </div>
          <h1 className={`text-3xl font-bold mb-2 ${statusInfo?.color}`}>
            {statusInfo?.title}
          </h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            {statusInfo?.description}
          </p>
        </div>

        {/* Quick Actions */}
        <div className="flex justify-center space-x-4 mb-8">
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh Status
          </button>
          
          {paymentData?.status === 'completed' && (
            <button
              onClick={handleDownloadReceipt}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              <Download className="h-4 w-4 mr-2" />
              Download Receipt
            </button>
          )}
          
          <button
            onClick={() => navigate('/smart-contact')}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
          >
            <MessageCircle className="h-4 w-4 mr-2" />
            Contact Support
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Timeline */}
          <div className="lg:col-span-2">
            <PaymentStatusTimeline 
              payment={paymentData}
              order={orderData}
            />
          </div>
          
          {/* Transaction Details */}
          <div>
            <TransactionDetails 
              payment={paymentData}
              order={orderData}
              paymentService={paymentService}
            />
          </div>
        </div>

        {/* Payment Actions */}
        <div className="mt-8">
          <PaymentActions
            payment={paymentData}
            order={orderData}
            onRetryPayment={handleRetryPayment}
            onScheduleService={() => navigate('/customer-portal')}
            onReorder={() => navigate('/e-commerce-store')}
          />
        </div>

        {/* Auto-refresh notice */}
        {(paymentData?.status === 'pending' || paymentData?.status === 'processing') && (
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-md p-4 text-center">
            <p className="text-sm text-blue-800">
              This page will automatically update when your payment status changes.
              You can also check your email for payment confirmation.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentStatusTracking;